import { createBrowserRouter, Navigate, RouteObject } from 'react-router';
import ProtectedRoute, {
  PublicRoute,
  NotFoundRoute,
} from '@components/routing/RouteWrappers';
import { Suspense, lazy } from 'react';

const LandingPage = lazy(() => import('./pages/LandingPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const WaitlistConfirmedPage = lazy(
  () => import('./pages/WaitlistConfirmedPage')
);
const EstimatePage = lazy(() => import('./pages/EstimatePage'));
const BookCallPage = lazy(() => import('./pages/BookCallPage'));
const BookingSuccessPage = lazy(() => import('./pages/BookingSuccessPage'));
const WaitlistSentPage = lazy(() => import('./pages/WaitlistSentPage'));
const SupportCallPage = lazy(() => import('./pages/SupportCallPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const InvestmentsPage = lazy(() => import('./pages/InvestmentsPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const ActionPages = lazy(() => import('./pages/ActionPages'));

const routes: RouteObject[] = [
  {
    path: '/',
    element: (
      <PublicRoute>
        <Suspense fallback={null}>
          <LandingPage />
        </Suspense>
      </PublicRoute>
    ),
  },
  {
    path: '/onboarding',
    element: (
      <PublicRoute>
        <Suspense fallback={null}>
          <Navigate to="/onboarding/step/1" replace />
        </Suspense>
      </PublicRoute>
    ),
  },
  {
    path: '/onboarding/step/:stepNumber',
    element: (
      <PublicRoute>
        <Suspense fallback={null}>
          <OnboardingPage />
        </Suspense>
      </PublicRoute>
    ),
  },
  {
    path: '/waitlist-sent',
    element: (
      <PublicRoute>
        <Suspense fallback={null}>
          <WaitlistSentPage />
        </Suspense>
      </PublicRoute>
    ),
  },
  {
    path: '/waitlisted',
    element: (
      <PublicRoute>
        <Suspense fallback={null}>
          <WaitlistConfirmedPage />
        </Suspense>
      </PublicRoute>
    ),
  },
  {
    path: '/estimate-sent',
    element: (
      <PublicRoute>
        <Suspense fallback={null}>
          <EstimatePage />
        </Suspense>
      </PublicRoute>
    ),
  },
  {
    path: '/estimate',
    element: (
      <PublicRoute>
        <Suspense fallback={null}>
          <EstimatePage />
        </Suspense>
      </PublicRoute>
    ),
  },
  {
    path: '/book-call',
    element: (
      <PublicRoute extraWide>
        <Suspense fallback={null}>
          <BookCallPage />
        </Suspense>
      </PublicRoute>
    ),
  },
  {
    path: '/support-call',
    element: (
      <PublicRoute extraWide>
        <Suspense fallback={null}>
          <SupportCallPage />
        </Suspense>
      </PublicRoute>
    ),
  },
  {
    // Send support call success to a different path for Google Ads tracking
    path: '/support-scheduled',
    element: (
      <PublicRoute>
        <Suspense fallback={null}>
          <BookingSuccessPage />
        </Suspense>
      </PublicRoute>
    ),
  },
  {
    path: '/booking-success',
    element: (
      <PublicRoute>
        <Suspense fallback={null}>
          <BookingSuccessPage />
        </Suspense>
      </PublicRoute>
    ),
  },
  {
    path: '/404',
    element: (
      <NotFoundRoute>
        <Suspense fallback={null}>
          <NotFoundPage />
        </Suspense>
      </NotFoundRoute>
    ),
  },
  {
    path: '*',
    element: <Navigate to="/404" replace />,
  },
  {
    path: '/dashboard',
    element: (
      <ProtectedRoute>
        <Suspense fallback={null}>
          <DashboardPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/investments',
    element: (
      <ProtectedRoute>
        <Suspense fallback={null}>
          <InvestmentsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/profile',
    element: (
      <ProtectedRoute>
        <Suspense fallback={null}>
          <ProfilePage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/login',
    element: (
      <PublicRoute>
        <Suspense fallback={null}>
          <LoginPage />
        </Suspense>
      </PublicRoute>
    ),
  },
  {
    path: '/support',
    element: (
      <ProtectedRoute>
        <Suspense fallback={null}>
          <SupportPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/actions/:actionId',
    element: (
      <ProtectedRoute>
        <Suspense fallback={null}>
          <ActionPages />
        </Suspense>
      </ProtectedRoute>
    ),
  },
];

export const router = createBrowserRouter(routes);
